import { asPositiveInt } from "@/lib/utils";

// ID-6524: the Data Review region / sub-region filters, as URL search params `regionId` and
// `subRegionId`. Kept free of component imports so the two rules can be unit-tested without
// rendering the filters; `asPositiveInt` is the same coercion the accountId search param uses.

export type RegionFilterValues = {
  regionId?: number | null;
  subRegionId?: number | null;
};

/**
 * A sub-region only makes sense under the region it was picked in: changing or clearing the region
 * drops it, re-selecting the same region keeps it.
 *
 * Dropped means `null`, not `undefined`. A react-hook-form Controller keeps showing the value it
 * mounted with whenever its field becomes undefined (useController's useWatch falls back to that
 * captured default), and the filter form is reset from the URL, so a deep-linked sub-region stayed on
 * screen after the region changed while the form value and the URL had moved on. `null` is a value
 * the Controller re-renders on; the URL layer (coerceRegionSearch) drops it before it is written.
 */
export const cascadeRegionFilter = (
  previous: RegionFilterValues,
  regionId: number | null | undefined,
): RegionFilterValues => ({
  regionId,
  subRegionId: regionId === previous.regionId ? previous.subRegionId : null,
});

/**
 * The URL pair, coerced and made coherent: a sub-region without a region is dropped. The cascade
 * in the filter form cannot cover this -- removing the "Region" chip writes the URL directly, and
 * left `subRegionId` applied while the popover showed the sub-region select disabled.
 */
export const coerceRegionSearch = (search: {
  regionId?: unknown;
  subRegionId?: unknown;
}): { regionId: number | undefined; subRegionId: number | undefined } => {
  const regionId = asPositiveInt(search.regionId);
  return {
    regionId,
    subRegionId:
      regionId === undefined ? undefined : asPositiveInt(search.subRegionId),
  };
};
